import Config from '../../config/Config'
import { mergeMap, map, catchError, withLatestFrom } from 'rxjs/operators'
import { of } from 'rxjs'
import { ajax } from 'rxjs/ajax'
import { jwtSelector, headersSelector } from '../auth/selectors'
import * as actions from './actions'
import { ofType } from 'redux-observable'
import queryString from '@promoboxx/query-string-babel'

export const getGroupRetailers = (action$, state$) => {
  return action$.pipe(
    ofType(actions.GET_GROUP_RETAILERS),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { fields, groupId, perPage, offset } = action.payload
      const jwt = jwtSelector(state) || action.payload.jwt

      if (!jwt) {
        throw new Error('Non-existent JWT auth')
      }

      const params = {
        fields,
        per_page: perPage,
        offset,
      }

      const queryParams = queryString.stringify(params, {
        arrayFormat: 'bracket',
        encode: true,
      })

      const settings = {
        method: 'GET',
        responseType: 'json',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        url: `${Config.API_BASE}/v2/groups/${groupId}/retailers?${queryParams}`,
      }

      return ajax(settings).pipe(
        map((payload) => {
          return {
            type: actions.GET_GROUP_RETAILERS_FULFILLED,
            payload: {
              retailers: payload.response,
              retailersTotal: parseInt(
                payload.xhr.getResponseHeader('x-total'),
              ),
              retailersFilteredTotal: parseInt(
                payload.xhr.getResponseHeader('x-filtered-total'),
              ),
              getGroupRetailersResponse: {
                success: true,
                error: false,
                loading: false,
              },
            },
          }
        }),
        catchError((payload) => {
          return of({
            type: actions.GET_GROUP_RETAILERS_REJECTED,
            payload: payload.response,
          })
        }),
      )
    }),
    catchError((payload) => {
      return of({
        type: actions.GET_GROUP_RETAILERS_REJECTED,
        payload: payload.response,
      })
    }),
  )
}

export const getSeatBrandRetailersBulkGet = (action$, state$) => {
  return action$.pipe(
    ofType(actions.GET_SEAT_BRAND_RETAILERS_BULK_GET),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const jwt = jwtSelector(state) || action.payload.jwt

      if (!jwt) {
        throw new Error('Non-existent JWT auth')
      }

      const defaultFields = [
        'formatted_phone',
        'facebook_pages',
        'twitter_accounts',
        'automations',
      ]

      const body = {
        fields: action.payload.fields
          ? [...action.payload.fields, ...defaultFields]
          : defaultFields,
        brand_id: action.payload.brand_id || null,
      }

      if (
        state$.value &&
        state$.value.retailers &&
        state$.value.retailers.seatTableParameters
      ) {
        body['search'] =
          state$.value.retailers.seatTableParameters.searchTerm || ''
        body['per_page'] =
          state$.value.retailers.seatTableParameters.rowsPerPage || 20
        body['offset'] =
          (state$.value.retailers.seatTableParameters.page || 0) *
          body['per_page']

        if (state$.value.retailers.seatTableParameters.retailer_ids) {
          body['filters'] = {
            by_ids: state$.value.retailers.seatTableParameters.retailer_ids,
          }
        }
      }

      const settings = {
        body,
        method: 'POST',
        responseType: 'json',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        url: `${Config.API_BASE}/v2/brands/${action.payload.brand_id}/retailers/bulk_get`,
      }

      return ajax(settings).pipe(
        map((payload) => {
          return {
            type: actions.GET_SEAT_BRAND_RETAILERS_BULK_GET_FULFILLED,
            payload: {
              seatTableRetailers: payload.response,
              seatTableRetailersTotal: parseInt(
                payload.xhr.getResponseHeader('x-total'),
              ),
              seatTableRetailersFilteredTotal: parseInt(
                payload.xhr.getResponseHeader('x-filtered-total'),
              ),
              getSeatBrandRetailersBulkGetResponse: {
                success: true,
                error: false,
                loading: false,
              },
            },
          }
        }),
        catchError((payload) => {
          return of({
            type: actions.GET_SEAT_BRAND_RETAILERS_BULK_GET_REJECTED,
            payload: {
              getSeatBrandRetailersBulkGetResponse: {
                success: false,
                error: payload.response,
                loading: false,
              },
            },
          })
        }),
      )
    }),
    catchError((error) => {
      console.warn(error)
      return of({
        type: actions.GET_SEAT_BRAND_RETAILERS_BULK_GET_REJECTED,
        payload: {
          getSeatBrandRetailersBulkGetResponse: {
            success: false,
            error,
            loading: false,
          },
        },
      })
    }),
  )
}

export const getBrandRetailers = (action$, state$) => {
  // Variables with $ indicate a stream...
  return action$.pipe(
    ofType(actions.GET_BRAND_RETAILERS),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const jwt = jwtSelector(state) || action.payload.jwt

      if (!jwt) {
        return of({
          type: actions.GET_BRAND_RETAILERS_REJECTED,
          payload: new Error('JWT is unavailable'),
        })
      }

      const { id } = action.payload

      if (!id) {
        return of({
          type: actions.GET_BRAND_RETAILERS_REJECTED,
          payload: new Error('Brand Id is unavailable'),
        })
      }

      const params = {
        brand_id: id,
        fields: action.payload.fields || [],
        filters: JSON.stringify(action.payload.filters) || null,
        filter_params: JSON.stringify(action.payload.filterParams) || null,
        ads_enabled: action.payload.adsEnabled || null,
        search: action.payload.search || null,
      }

      const queryParams = queryString.stringify(params, {
        arrayFormat: 'bracket',
        encode: true,
      })

      const settings = {
        method: 'GET',
        responseType: 'json',
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
        url: `${Config.API_BASE}/v2/brands/${id}/retailers?${queryParams}`,
      }

      return ajax(settings).pipe(
        map((payload) => {
          return {
            type: actions.GET_BRAND_RETAILERS_FULFILLED,
            payload: { retailers: payload.response },
          }
        }),
        catchError((payload) => {
          return of({
            type: actions.GET_BRAND_RETAILERS_REJECTED,
            payload: { error: payload.response },
          })
        }),
      )
    }),
    catchError((error) => {
      return of({
        type: actions.GET_BRAND_RETAILERS_REJECTED,
        payload: { error },
      })
    }),
  )
}

export const getFacebookPages = (action$, state$) => {
  return action$.pipe(
    ofType(actions.GET_FACEBOOK_PAGES),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { retailerId } = action.payload
      const headers = headersSelector(state)

      if (!headers) {
        throw new Error('Bad or Non-existent auth headers')
      }

      if (!retailerId) {
        throw new Error('Retailer ID is required')
      }

      const settings = {
        method: 'GET',
        responseType: 'json',
        headers,
        url: `${Config.API_BASE}/v2/retailers/${retailerId}/facebook_pages`,
      }

      return ajax(settings).pipe(
        map((payload) => {
          return {
            type: actions.GET_FACEBOOK_PAGES_FULFILLED,
            payload: payload.response,
          }
        }),
        catchError((payload) => {
          return of({
            type: actions.GET_FACEBOOK_PAGES_REJECTED,
            payload: payload.response,
          })
        }),
      )
    }),
    catchError((payload) => {
      return of({
        type: actions.GET_FACEBOOK_PAGES_REJECTED,
        payload,
      })
    }),
  )
}
